import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  DeleteSweep as DeleteSweepIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RestartAlt as RestartAltIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useResetPots } from '../../../features/development/hooks/useResetPots';

export const PotsDebugSection: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useResetPots();

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  const handleConfirmReset = () => {
    setConfirmOpen(false);
    mutation.mutate();
  };

  // Auto-clear success/error after 8 seconds (longer than jobs since more info to read)
  React.useEffect(() => {
    if (mutation.isSuccess || mutation.isError) {
      const timer = setTimeout(() => {
        mutation.reset();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [mutation.isSuccess, mutation.isError, mutation]);

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <RestartAltIcon color="primary" />
          <Typography variant="h6">Pot Data Reset</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Reset all pot data for testing pot transitions from scratch
        </Typography>
      </Box>

      {/* Warning */}
      <Alert severity="warning" icon={<WarningIcon />}>
        <Typography variant="body2">
          <strong>Destructive action:</strong> This will permanently delete all
          allocations, deductions, pots, periods, and related events for your
          organisation. Time will also be reset to system time.
        </Typography>
      </Alert>

      {/* Reset Action */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reset All Pot Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this after testing pot transitions to clear all data and reset
          time back to the current system time. This allows you to start fresh
          without reseeding the database.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteSweepIcon />
              )
            }
            onClick={handleOpenConfirm}
            disabled={mutation.isPending}
            sx={{ minWidth: 200 }}
          >
            {mutation.isPending ? 'Resetting...' : 'Reset Pot Data'}
          </Button>

          {/* Success Message */}
          {mutation.isSuccess && mutation.data && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ flex: 1, py: 0.5 }}
            >
              <Typography variant="body2">
                Reset complete: {mutation.data.deletedAllocations} allocations,{' '}
                {mutation.data.deletedDeductions} deductions,{' '}
                {mutation.data.deletedPots} pots, {mutation.data.deletedPeriods}{' '}
                periods deleted. Time reset to system time.
              </Typography>
            </Alert>
          )}

          {/* Error Message */}
          {mutation.isError && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ flex: 1, py: 0.5 }}
            >
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Failed to reset pot data'}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-reset-dialog-title"
        aria-describedby="confirm-reset-dialog-description"
      >
        <DialogTitle id="confirm-reset-dialog-title">
          Confirm Pot Data Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-reset-dialog-description">
            Are you sure you want to reset all pot data? This will delete:
            <Box component="ul" sx={{ mt: 1, mb: 0 }}>
              <li>All allocations (booked work)</li>
              <li>All deductions</li>
              <li>All payment pots</li>
              <li>All pot periods</li>
              <li>All related events</li>
            </Box>
            <Box sx={{ mt: 2 }}>
              Organisation time will be reset to the current system time.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReset}
            color="error"
            variant="contained"
            autoFocus
          >
            Reset All Data
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
